<!-- =========================================================================================
    File Name: tableFilterSorter.vue
    Description: Add filter and sorting functionality to table
    ----------------------------------------------------------------------------------------
    Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
      Author: Pixinvent
    Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->


<template>
  <div>
    <b-overlay
      :show="show"
      :variant="variant"
      :opacity="opacity"
      :blur="blur"
      rounded="lg"
      spinner-variant="primary"
    >
      <b-card class="mt-5">
        <h4 class="heading">
          {{ $t("Appointments") }}
          <feather-icon
            id="export"
            icon="DownloadCloudIcon"
            svgClasses="w-5 h-5 hover:text-danger stroke-current"
            class="ml-5"
            :title="$t('Export')"
            v-b-modal.modal-1
          />
        </h4>

        <b-modal
          id="modal-1"
          :title="$t('ExportToExcel')"
          :ok-title="$t('Export')"
          :cancel-title="$t('Cancel')"
          @ok="exportToExcel"
          @cancel="clearFields"
          @close="clearFields"
          class="export-options"
          centered
        >
          <b-input
            v-model="fileName"
            :placeholder="$t('FileName')"
            class="w-full"
          />
          <v-select v-model="selectedFormat" :options="formats" class="my-4"
            ><template v-slot:option="option">
              <span :class="option.icon">{{ option.label }} </span>
              <feather-icon
                v-if="selectedFormat == option.label"
                icon="CheckIcon"
                class="float-right"
                svgClasses="w-5 h-5"
              />
            </template>
          </v-select>
          <div class="flex walk-card">
            <span class="mr-4">{{ $t("CellAutoWidth") }}:</span>
            <b-form-checkbox
              v-model="cellAutoWidth"
              size="lg"
              name="cell_width"
              switch
            >
            </b-form-checkbox>
          </div>
        </b-modal>

        <div class="export-table">
          <div class="vx-row">
            <div class="vx-col w-1/2">
              <label for class="vs-input--label"
                >{{ $t("StartDate") }} <span class="red">*</span></label
              >

              <b-form-datepicker
                size="large"
                v-validate="'required'"
                v-model="start_date"
                name="start_date"
                format="dd/MM/yyyy"
                class="p-1 datepick"
                :show-decade-nav="showDecadeNav"
                :hide-header="hideHeader"
                :date-format-options="{
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                }"
              ></b-form-datepicker>
            </div>
            <div class="vx-col w-1/2">
              <label for class="vs-input--label"
                >{{ $t("EndDate") }} <span class="red">*</span></label
              >

              <b-form-datepicker
                size="large"
                v-validate="'required'"
                v-model="end_date"
                name="end_date"
                format="dd/MM/yyyy"
                class="p-1 datepick"
                :show-decade-nav="showDecadeNav"
                :hide-header="hideHeader"
                :date-format-options="{
                  year: 'numeric',
                  month: 'numeric',
                  day: 'numeric',
                }"
              ></b-form-datepicker>
            </div>
          </div>
          <div class="mb-5 mt-5">
            <strong>
              {{ $t("TotalAppointments") }}: {{ appointments.length }}</strong
            >
          </div>

          <b-col lg="5" class="my-1 p-2 float-right">
            <b-form-group
              label-for="filter-input"
              label-cols-sm="3"
              label-align-sm="right"
              label-size="sm"
              class="mb-0"
            >
              <b-input-group size="md">
                <b-input-group-prepend is-text>
                  <b-icon icon="search"></b-icon>
                </b-input-group-prepend>
                <b-form-input
                  id="filter-input"
                  v-model="filter"
                  type="search"
                  :placeholder="$t('Search')"
                ></b-form-input>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-table
            :current-page="currentPage"
            :per-page="perPage"
            :items="appointments"
            :fields="fields"
            :busy="isBusy"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :sort-direction="sortDirection"
            responsive
            show-empty
            :filter="filter"
            :filter-included-fields="filterOn"
            :empty-text="$t('EmptyData')"
            :empty-filtered-text="$t('EmptyFilter')"
            @filtered="onFiltered"
          >
            <template #head()="data">
              <span>{{ $t(data.label) }}</span>
            </template>
            <template #cell(action)="data">
              <feather-icon
                v-b-tooltip.hover
                :title="$t('Show')"
                icon="EyeIcon"
                svgClasses="w-5 h-5 hover:text-danger stroke-current"
                @click="
                  $router.push('/company/appointment/show/' + data.item.id)
                "
              />

              <feather-icon
                v-if="
                  data.item.current_status == '0' &&
                  (data.item.is_confirmed == 1 ||
                    data.item.automatic_booking_confirmed)
                "
                v-b-tooltip.hover
                :title="$t('Complete')"
                id="status"
                svgClasses="w-5 h-5 hover:text-danger stroke-current"
                class="ml-2"
                :icon="
                  data.item.current_status == '0'
                    ? 'StopCircleIcon'
                    : 'DiscIcon'
                "
                svg-classes="stroke-current text-primary"
                @click="updateStatus(data.item.id)"
              />
              <feather-icon
                v-b-tooltip.hover
                :title="$t('Cancel')"
                v-if="data.item.current_status == 0"
                @click="display(data.item.id)"
                icon="TrashIcon"
                svgClasses="w-5 h-5 hover:text-danger stroke-current"
                class="ml-1"
              />
            </template>
          </b-table>
          <b-pagination
            v-if="rows > perPage"
            v-model="currentPage"
            :total-rows="totalRows"
            :per-page="perPage"
            aria-controls="my-table"
            class="float-right"
          ></b-pagination>
        </div>
      </b-card>

      <b-modal
        id="custom-modal"
        v-model="activePromptCancel"
        :title="$t('CancelAppointment')"
        :ok-title="$t('CancelAppointment')"
        :cancel-title="$t('Cancel')"
        @ok="cancelApp"
        @close="close"
        :is-valid="check"
        centered
      >
        <!-- <vs-prompt
      @cancel="close"
      :title="$t('CancelAppointment')"
      @close="close"
      :active.sync="activePromptCancel"
      @accept="cancelApp()"
      :is-valid="check"
      :accept-text="$t('CancelAppointment')"
      :cancel-text="$t('Cancel')"
    > -->
        <div class="con-exemple-prompt">
          <div class="vx-col md:w-full w-full">
            <strong>
              <!-- <label>{{ $t("Reason") }}</label> -->
            </strong>
            <b-textarea
              v-model="message"
              rows="2"
              :label="$t('Reason')"
              name="reason"
            />
          </div>
        </div>
      </b-modal>
    </b-overlay>
  </div>
</template>

<script>
import vSelect from "vue-select";
import Datepicker from "vuejs-datepicker";
import moment from "moment";

export default {
  components: {
    vSelect,
    Datepicker,
  },
  data() {
    return {
      fileName: "",
      formats: ["xlsx", "csv", "txt"],
      cellAutoWidth: true,
      selectedFormat: "xlsx",
      headerTitle: ["User", "Day", "Date", "StartTime", "EndTime"],
      headerVal: ["users", "day", "date", "start_time", "end_time"],
      appointments: [],
      activePrompt: false,
      popupActivo: false,
      start_date: moment().clone().startOf("month").format("MM/DD/YYYY"),
      end_date: moment().format("MM/DD/YYYY"),
      totalRows: 1,
      cancel_id: "",
      activePromptCancel: false,
      message: "",
      check: false,
      perPage: 10,
      currentPage: 1,
      sortBy: "",
      sortDesc: false,
      sortDirection: "asc",
      filter: null,
      filterOn: [],
      show: false,
      variant: "light",
      opacity: 0.85,
      blur: "2px",
      error: "",
      success: "",
      sec: 2,
      counter: 0,
      isBusy: false,
      fields: [
        {
          key: "date",
          label: "Date",
          sortable: true,
          sortDirection: "desc",
        },
        {
          key: "start_time",
          label: "StartTime",
          sortable: true,
          sortDirection: "desc",
        },
        {
          key: "end_time",
          label: "EndTime",
          sortable: true,
          sortDirection: "desc",
        },
        {
          key: "users",
          label: "User",
          sortable: true,
          sortDirection: "desc",
        },
        {
          key: "current_status",
          label: "Status",
          sortable: true,
          sortDirection: "desc",
        },
        { key: "action", label: "Action" },
      ],
      //new changes 03-01-2022
      showDecadeNav: true,
      hideHeader: true,
    };
  },
  computed: {
    is_unio() {
      return this.$store.getters.currentUser.is_unio;
    },
    rows() {
      return this.appointments.length;
    },
  },
  watch: {
    start_date(value) {
      this.getAppointments();
    },
    end_date(value) {
      this.getAppointments();
    },
    message(value) {
      if (value != "") {
        this.check = true;
      } else {
        this.check = false;
      }
    },
  },
  created() {
    this.getAppointments();
  },

  methods: {
    onFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    },
    /**
     *  exportToExcel method for export file according to format
     **/
    exportToExcel() {
      import("@/assets/utils/export").then((excel) => {
        const list = this.appointments;
        this.headerTitle.map((title, index) => {
          this.headerTitle[index] = this.$t(title);
        });

        const data = this.formatJson(this.headerVal, list);
        excel.export_json_to_excel({
          header: this.headerTitle,
          data,
          filename: this.fileName,
          autoWidth: this.cellAutoWidth,
          bookType: this.selectedFormat,
        });
        this.headerTitle = ["User", "Day", "Date", "StartTime", "EndTime"];
        this.clearFields();
      });
    },
    /**
     *  @params filterVal ,jsonData
     * formatJson   method  for export file according to format
     **/
    formatJson(filterVal, jsonData) {
      return jsonData.map((v) =>
        filterVal.map((j) => {
          return v[j];
        })
      );
    },
    /**
     *
     * clearFields   method  for export selected format
     **/
    clearFields() {
      this.filename = "";
      this.cellAutoWidth = true;
      this.selectedFormat = "xlsx";
    },

    /**
     * getAppointments
     **/
    getAppointments() {
      this.show = true;
      let data = {
        inmate_id: this.$route.params.id,
        start_date: moment(this.start_date).format("YYYY-MM-DD"),
        end_date: moment(this.end_date).format("YYYY-MM-DD"),
      };
      this.$store
        .dispatch("getInMateAppointments", data)
        .then((res) => {
          this.show = false;
          this.appointments = res.data.data;
          this.totalRows = this.appointments.length;  
        })
        .catch((error) => {
          this.$log.error(
            "File : appointments.vue | Function : getAppointments() | Error :",
            error
          );
          this.show = false;
          // Error response
          this.error =
            this.$t(error.replace(/[\s\/]/g, "")) !==
            error.replace(/[\s\/]/g, "")
              ? this.$t(error.replace(/[\s\/]/g, ""))
              : error;
          this.$toasted.show(this.error, {
            className: "top-alert",
            position: "top-center",
            type: "error",
            duration: 2000,
          });
          return false;
        });
    },

    /**
     * update status completed
     */
    updateStatus(appid) {
      this.$swal({
        text: this.$t("WantToCompletedStatus"),

        type: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("Yes"),
        confirmButtonColor: "#7367F0",
        cancelButtonText: this.$t("No"),
        showCloseButton: true,
        showLoaderOnConfirm: true,
      })
        .then((result) => {
          if (result.value) {
            this.show = true;

            var data = {
              status: 1,
              id: appid,
            };

            this.$store.dispatch("StaffUpdateStatus", data).then((res) => {
              this.show = false;
              this.getAppointments();

              this.$toasted.show(this.$t("AppointmentMarkedCompleted"), {
                className: "top-alert",
                position: "top-center",
                type: "success",
                duration: 2000,
              });
            });
          }
        })
        .catch((error) => {
          this.$log.error(
            "File : List.vue | Function : updateStatus() | Error :",
            error
          );
          this.show = false;
          // Error response
          this.error =
            this.$t(error.replace(/[\s\/]/g, "")) !==
            error.replace(/[\s\/]/g, "")
              ? this.$t(error.replace(/[\s\/]/g, ""))
              : error;
          this.$toasted.show(this.error, {
            className: "top-alert",
            position: "top-center",
            type: "error",
            duration: 2000,
          });
          return false;
        });
    },

    /**
     *
     * Display   method  for cancelAppointment from listing
     **/
    display(id) {
      this.cancel_id = id;
      this.activePromptCancel = true;
    },
    /**
     *
     * close   method  for close dialog box
     **/
    close() {
      this.message = "";
      this.check = false;
    },

    /**
     *
     * submitForm   method  for cancelAppointment from listing
     **/
    cancelApp() {
      this.show = true;
      var data = {
        reason: this.message,
        id: this.cancel_id,
      };
      this.$store.dispatch("CancelAppointment", data).then((res) => {
        this.message = "";
        this.show = false;
        this.getAppointments();
        this.$toasted.show(this.$t("AppointmentCanceledSucess"), {
          className: "top-alert",
          position: "top-center",
          type: "success",
          duration: 2000,
        });
      });
    },
  },
};
</script>

<style lang="stylus" scope>
.heading {
  margin-bottom: 20px;
}

.add {
  position: absolute;
  margin-top: 10px;
}

.action {
  margin-left: 20px;
  display: inline;
}

.con-vs-tooltip {
  display: inline;
}
</style>